import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, ChevronLeft, ChevronRight, Loader2, CalendarIcon } from "lucide-react";
import { useMarketingEvents, type MarketingEvent } from "@/hooks/use-marketing-events";
import { MarketingEventModal } from "@/components/crm/MarketingEventModal";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { format, getISOWeek, startOfMonth, endOfMonth, parseISO, isSameDay } from "date-fns";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function getISOWeekNum(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getWeeksForYear(year: number) {
  const monthWeeks: { month: string; weeks: number[] }[] = MONTHS.map((m) => ({
    month: m,
    weeks: [],
  }));

  for (let week = 1; week <= 52; week++) {
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    const thursday = new Date(monday);
    thursday.setDate(monday.getDate() + 3);
    const monthIndex = thursday.getMonth();
    monthWeeks[monthIndex].weeks.push(week);
  }

  return monthWeeks;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  post: "bg-blue-100 text-blue-800 border-blue-200",
  release: "bg-green-100 text-green-800 border-green-200",
  website: "bg-purple-100 text-purple-800 border-purple-200",
  event: "bg-orange-100 text-orange-800 border-orange-200",
  campaign: "bg-pink-100 text-pink-800 border-pink-200",
  general: "bg-gray-100 text-gray-800 border-gray-200",
};

const MarketingCalendar = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<MarketingEvent | null>(null);
  const [defaultWeek, setDefaultWeek] = useState<number | undefined>();
  const [defaultDate, setDefaultDate] = useState<Date | undefined>();
  const [calendarMonth, setCalendarMonth] = useState(new Date(year, new Date().getMonth()));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>();
  const [activeTab, setActiveTab] = useState("summary");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { eventsQuery, createEvent, updateEvent, deleteEvent } = useMarketingEvents(year);
  const monthWeeks = useMemo(() => getWeeksForYear(year), [year]);
  const currentWeek = getISOWeekNum(new Date());

  const scrollToCurrentWeek = useCallback(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-week="${currentWeek}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center" });
  }, [currentWeek]);

  useEffect(() => {
    if (activeTab === "summary" && year === currentYear) {
      const timer = setTimeout(scrollToCurrentWeek, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, year, currentYear, scrollToCurrentWeek]);

  const eventsByWeek = useMemo(() => {
    const map: Record<number, MarketingEvent[]> = {};
    eventsQuery.data?.forEach((evt) => {
      if (!map[evt.week_number]) map[evt.week_number] = [];
      map[evt.week_number].push(evt);
    });
    return map;
  }, [eventsQuery.data]);

  // Events indexed by date string for calendar view
  const eventsByDate = useMemo(() => {
    const map: Record<string, MarketingEvent[]> = {};
    eventsQuery.data?.forEach((evt) => {
      if (evt.event_date) {
        if (!map[evt.event_date]) map[evt.event_date] = [];
        map[evt.event_date].push(evt);
      }
    });
    return map;
  }, [eventsQuery.data]);

  // Upcoming events (next 5)
  const upcomingEvents = useMemo(() => {
    if (!eventsQuery.data) return [];
    const today = new Date();
    const todayStr = format(today, "yyyy-MM-dd");
    return eventsQuery.data
      .filter((evt) => {
        if (evt.event_date) return evt.event_date >= todayStr;
        return evt.year === currentYear && evt.week_number >= currentWeek;
      })
      .sort((a, b) => {
        const dateA = a.event_date || `${a.year}-W${String(a.week_number).padStart(2, "0")}`;
        const dateB = b.event_date || `${b.year}-W${String(b.week_number).padStart(2, "0")}`;
        return dateA.localeCompare(dateB);
      })
      .slice(0, 5);
  }, [eventsQuery.data, currentYear, currentWeek]);

  // Events for selected date in calendar view
  const selectedDateEvents = useMemo(() => {
    if (!selectedCalendarDate) return [];
    const key = format(selectedCalendarDate, "yyyy-MM-dd");
    return eventsByDate[key] || [];
  }, [selectedCalendarDate, eventsByDate]);

  // All dates that have events (for calendar dot indicators)
  const eventDates = useMemo(() => {
    return Object.keys(eventsByDate).map(d => parseISO(d));
  }, [eventsByDate]);

  const handleCellClick = (week: number) => {
    setEditingEvent(null);
    setDefaultWeek(week);
    setDefaultDate(undefined);
    setModalOpen(true);
  };

  const handleCalendarDateClick = (date: Date | undefined) => {
    setSelectedCalendarDate(date);
  };

  const handleAddFromCalendar = () => {
    setEditingEvent(null);
    setDefaultDate(selectedCalendarDate || new Date());
    setDefaultWeek(selectedCalendarDate ? getISOWeek(selectedCalendarDate) : undefined);
    setModalOpen(true);
  };

  const handleEventClick = (evt: MarketingEvent, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingEvent(evt);
    setDefaultWeek(evt.week_number);
    setDefaultDate(evt.event_date ? new Date(evt.event_date) : undefined);
    setModalOpen(true);
  };

  const handleSave = (data: any) => {
    if (editingEvent) {
      updateEvent.mutate({ id: editingEvent.id, ...data }, {
        onSuccess: () => setModalOpen(false),
      });
    } else {
      createEvent.mutate(data, {
        onSuccess: () => setModalOpen(false),
      });
    }
  };

  const handleDelete = () => {
    if (editingEvent) {
      deleteEvent.mutate(editingEvent.id, {
        onSuccess: () => setModalOpen(false),
      });
    }
  };


  const renderStaffNames = (evt: MarketingEvent) => {
    if (evt.event_staff && evt.event_staff.length > 0) {
      return evt.event_staff
        .map(s => s.staff ? `${s.staff.first_name} ${s.staff.last_name}` : "")
        .filter(Boolean)
        .join(", ");
    }
    if (evt.staff) {
      return `${evt.staff.first_name} ${evt.staff.last_name}`;
    }
    return null;
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <SidebarInset>
          <FixedPageHeader
            {...PAGE_HEADERS.marketingCalendar}
            actions={
              <div className="flex items-center gap-3">
                <TabsList>
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="calendar">Calendar</TabsTrigger>
                </TabsList>
                <Button variant="outline" size="sm" onClick={scrollToCurrentWeek}>
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  Today
                </Button>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setYear(y => y - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={() => setYear(y => y + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button onClick={() => { setEditingEvent(null); setDefaultWeek(undefined); setDefaultDate(undefined); setModalOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Event
                </Button>
              </div>
            }
          />
          <div className="p-4" style={{ paddingTop: "8.5rem" }}>
            {eventsQuery.isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>

                {/* SUMMARY VIEW */}
                <TabsContent value="summary">
                  <ScrollArea className="w-full" ref={scrollRef}>
                    <div className="min-w-[1800px]">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            {monthWeeks.map((mw) => (
                              <th
                                key={mw.month}
                                colSpan={mw.weeks.length}
                                className="border border-border bg-base-beige px-2 py-2 text-center text-sm font-semibold text-foreground"
                              >
                                {mw.month}
                              </th>
                            ))}
                          </tr>
                          <tr>
                            {monthWeeks.flatMap((mw) =>
                              mw.weeks.map((w) => (
                                <th
                                  key={w}
                                  data-week={w}
                                  className={`border border-border px-1 py-1 text-center text-xs font-medium min-w-[90px] ${
                                    year === currentYear && w === currentWeek
                                      ? "bg-teal-extraLight text-primary font-bold"
                                      : "bg-muted/50 text-muted-foreground"
                                  }`}
                                >
                                  W{w}
                                </th>
                              ))
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {monthWeeks.flatMap((mw) =>
                              mw.weeks.map((w) => {
                                const weekEvents = eventsByWeek[w] || [];
                                return (
                                  <td
                                    key={w}
                                    className={`border border-border p-1 align-top min-h-[120px] h-32 cursor-pointer hover:bg-muted/30 transition-colors ${
                                      year === currentYear && w === currentWeek ? "bg-teal-extraLight/50" : ""
                                    }`}
                                    onClick={() => handleCellClick(w)}
                                  >
                                    <div className="space-y-1">
                                      {weekEvents.map((evt) => {
                                        const staffNames = renderStaffNames(evt);
                                        return (
                                          <div
                                            key={evt.id}
                                            className={`rounded border p-1.5 text-xs cursor-pointer hover:shadow-sm transition-shadow ${
                                              EVENT_TYPE_COLORS[evt.event_type] || EVENT_TYPE_COLORS.general
                                            }`}
                                            onClick={(e) => handleEventClick(evt, e)}
                                          >
                                            <div className="font-semibold truncate">{evt.title}</div>
                                            {evt.event_date && (
                                              <div className="text-[10px] mt-0.5 opacity-75">
                                                {format(parseISO(evt.event_date), "MMM d")}
                                              </div>
                                            )}
                                            <Badge variant="outline" className="text-[10px] px-1 py-0 mt-0.5">
                                              {evt.event_type}
                                            </Badge>
                                            {staffNames && (
                                              <div className="text-[10px] mt-0.5 opacity-75 truncate">
                                                {staffNames}
                                              </div>
                                            )}
                                            {evt.description && (
                                              <div className="text-[10px] mt-0.5 opacity-60 line-clamp-2">
                                                {evt.description}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                );
                              })
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </TabsContent>

                {/* CALENDAR VIEW */}
                <TabsContent value="calendar">
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="border border-border rounded-lg p-4 bg-base-beige/50">
                      <Calendar
                        mode="single"
                        selected={selectedCalendarDate}
                        onSelect={handleCalendarDateClick}
                        month={calendarMonth}
                        onMonthChange={setCalendarMonth}
                        className="p-3 pointer-events-auto"
                        modifiers={{
                          hasEvent: eventDates,
                        }}
                        modifiersClassNames={{
                          hasEvent: "bg-teal-extraLight font-bold",
                        }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-foreground">
                          {selectedCalendarDate
                            ? format(selectedCalendarDate, "EEEE, MMMM d, yyyy")
                            : "Select a date"}
                        </h3>
                        <Button size="sm" onClick={handleAddFromCalendar}>
                          <Plus className="h-4 w-4 mr-1" />
                          Add Event
                        </Button>
                      </div>

                      {selectedCalendarDate && selectedDateEvents.length === 0 && (
                        <p className="text-sm text-muted-foreground">No events on this date.</p>
                      )}

                      <div className="space-y-3">
                        {selectedDateEvents.map((evt) => {
                          const staffNames = renderStaffNames(evt);
                          return (
                            <div
                              key={evt.id}
                              className={`rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow ${
                                EVENT_TYPE_COLORS[evt.event_type] || EVENT_TYPE_COLORS.general
                              }`}
                              onClick={() => handleEventClick(evt)}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold">{evt.title}</h4>
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {evt.event_type}
                                  </Badge>
                                </div>
                                <span className="text-xs opacity-60">W{evt.week_number}</span>
                              </div>
                              {staffNames && (
                                <p className="text-sm mt-2 opacity-75">{staffNames}</p>
                              )}
                              {evt.description && (
                                <p className="text-sm mt-2 opacity-70">{evt.description}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Upcoming events without a specific date selected */}
                      {!selectedCalendarDate && eventsQuery.data && eventsQuery.data.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-3">All events this year</h4>
                          <div className="space-y-2">
                            {eventsQuery.data.map((evt) => {
                              const staffNames = renderStaffNames(evt);
                              return (
                                <div
                                  key={evt.id}
                                  className={`rounded border p-3 cursor-pointer hover:shadow-sm transition-shadow text-sm ${
                                    EVENT_TYPE_COLORS[evt.event_type] || EVENT_TYPE_COLORS.general
                                  }`}
                                  onClick={() => handleEventClick(evt)}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{evt.title}</span>
                                    <span className="text-xs opacity-60">
                                      {evt.event_date ? format(parseISO(evt.event_date), "MMM d") : `W${evt.week_number}`}
                                    </span>
                                  </div>
                                  {staffNames && (
                                    <div className="text-xs mt-1 opacity-75">{staffNames}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
                {/* UPCOMING EVENTS */}
                {upcomingEvents.length > 0 && (
                  <div className="mt-6 max-w-2xl">
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarIcon className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Upcoming Events</h3>
                    </div>
                    <div className="space-y-2">
                      {upcomingEvents.map((evt) => {
                        const staffNames = renderStaffNames(evt);
                        return (
                          <div
                            key={evt.id}
                            className="flex items-start gap-4 rounded-lg border-l-4 border-lime-button bg-card p-3 cursor-pointer hover:shadow-sm transition-shadow"
                            onClick={() => handleEventClick(evt)}
                          >
                            <div className="flex-shrink-0 w-16 text-center">
                              {evt.event_date ? (
                                <>
                                  <div className="text-xs text-muted-foreground">{format(parseISO(evt.event_date), "MMM")}</div>
                                  <div className="text-lg font-bold text-foreground">{format(parseISO(evt.event_date), "d")}</div>
                                </>
                              ) : (
                                <>
                                  <div className="text-xs text-muted-foreground">Week</div>
                                  <div className="text-lg font-bold text-foreground">{evt.week_number}</div>
                                </>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-foreground truncate">{evt.title}</span>
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${EVENT_TYPE_COLORS[evt.event_type] || EVENT_TYPE_COLORS.general}`}>
                                  {evt.event_type}
                                </Badge>
                              </div>
                              {staffNames && (
                                <p className="text-xs text-muted-foreground mt-0.5">{staffNames}</p>
                              )}
                              {evt.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{evt.description}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </SidebarInset>
      </div>

      <MarketingEventModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        event={editingEvent}
        defaultYear={year}
        defaultWeek={defaultWeek}
        defaultDate={defaultDate}
        onSave={handleSave}
        onDelete={editingEvent ? handleDelete : undefined}
        isSaving={createEvent.isPending || updateEvent.isPending || deleteEvent.isPending}
      />
    </SidebarProvider>
    </Tabs>
  );
};

export default MarketingCalendar;
